import { createReducer } from '@reduxjs/toolkit';
import {
  fetchLocationView,
  resetMapState,
  setMapBoundingBox,
  setMapError,
  setMapLoading,
  setMapLocation,
  setMapLocationLoaded,
  setMapMessage,
  zoomIn,
} from '../actions/mapV2';

/**
 * @typedef {import ('../propTypes/map/location').Location} Location
 * @typedef {import ('../propTypes/map/location').BoundingBox} BoundingBox
 * @typedef {import ('../propTypes/locationView').LocationView} LocationView
 */

/**
 * @typedef {Object} MapV2State
 * @property {Location} location
 * @property {BoundingBox} boundingBox
 * @property {{ message: string, status?: number}} [error]
 * @property {boolean} locationLoaded
 * @property {boolean} loading
 * @property {string} message
 * @property {LocationView} locationView
 */

/**
 * @type {MapV2State}
 */
export const initialState = {
  location: {
    center: { lat: 0, lon: 0 },
    zoom: 12,
  },
  locationLoaded: false,
  boundingBox: null,
  error: null,
  loading: false,
  message: '',
  locationView: {
    loading: false,
    taxonomy: {
      location: {
        coordinates: [],
      },
    },
    breadCrumbs: [],
    boundingBox: null,
    travelContent: null,
    siblings: [],
    url: '',
  },
};

const mapV2Reducer = createReducer(initialState, (builder) => {
  builder.addCase(setMapLocation, (state, action) => ({
    ...state,
    location: action.payload,
    locationLoaded: true,
  }));

  builder.addCase(setMapLocationLoaded, (state) => ({
    ...state,
    locationLoaded: true,
  }));

  builder.addCase(setMapBoundingBox, (state, action) => ({
    ...state,
    boundingBox: action.payload,
  }));
  builder.addCase(setMapError, (state, action) => {
    if (action.payload) {
      return {
        ...state,
        error: action.payload,
      };
    }
    return {
      ...state,
      error: null,
    };
  });
  builder.addCase(setMapLoading, (state, action) => ({
    ...state,
    loading: action.payload,
  }));
  builder.addCase(setMapMessage, (state, action) => ({
    ...state,
    message: action.payload,
  }));

  builder.addCase(fetchLocationView.pending, (state) => ({
    ...state,
    locationView: {
      ...state.locationView,
      loading: true,
    },
  }));

  builder.addCase(fetchLocationView.fulfilled, (state, action) => ({
    ...state,
    locationView: {
      ...state.locationView,
      loading: false,
      ...action.payload,
    },
  }));

  builder.addCase(fetchLocationView.rejected, (state, action) => ({
    ...state,
    locationView: {
      ...state.locationView,
      loading: false,
      error: action.error,
    },
  }));

  builder.addCase(resetMapState, () => initialState);

  builder.addCase(zoomIn, (state, action) => ({
    ...state,
    location: {
      ...state.location,
      center: action.payload || state.location.center,
      zoom: state.location.zoom + 1,
    },
  }));
});

export default mapV2Reducer;
